import React from 'react';
import { tokenService } from '../../services/tokenService';

type ExecutionStepStatus = 'pending' | 'in_progress' | 'complete' | 'error';
type ExecutionStepType = 'planner_decision' | 'tool_call' | 'tool_result' | 'executor_content';

export interface ExecutionStep {
  id: string;
  type: ExecutionStepType;
  status: ExecutionStepStatus;
  timestamp: number;
  iteration?: number;
  duration?: number;
  data?: any;
}

export interface StreamingMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  provider?: string;
  model?: string;
  toolCalls?: Array<any>;
  documents?: any[];
  latency?: number;
  error?: string;
  isStreaming?: boolean;
  streamingContent?: string;
  toolCallsInProgress?: string[];
  plannerIteration?: number;
  plannerDecision?: any;
}

type ToolPayload = {
  name: string;
  description: string;
  type: string;
  enabled: boolean;
  schema?: any;
  category?: string;
};

export interface StartStreamParams {
  provider: string;
  model: string;
  message: string;
  personId: string;
  tools: ToolPayload[];
}

export interface StreamFinalize {
  finalMessage: StreamingMessage | null;
  payload: any;
  executionSteps: ExecutionStep[];
}

interface UseAgentStreamingOptions {
  endpoint?: string;
  stallTimeoutMs?: number;
  watchdogIntervalMs?: number;
}

const DEFAULT_ENDPOINT = '/rpc/v1/agent/native/steps/agent.native.chat/stream';
const DEFAULT_STALL_TIMEOUT = 15000;
const DEFAULT_WATCHDOG_INTERVAL = 5000;

export function useAgentStreaming(options: UseAgentStreamingOptions = {}) {
  const endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
  const stallTimeoutMs = options.stallTimeoutMs ?? DEFAULT_STALL_TIMEOUT;
  const watchdogIntervalMs = options.watchdogIntervalMs ?? DEFAULT_WATCHDOG_INTERVAL;

  const [isStreaming, setIsStreaming] = React.useState(false);
  const [executionSteps, setExecutionSteps] = React.useState<ExecutionStep[]>([]);
  const [currentMessage, setCurrentMessage] = React.useState<StreamingMessage | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [_startedAt, setStartedAt] = React.useState<number | null>(null);
  const [lastEventAt, setLastEventAt] = React.useState<number | null>(null);

  const abortRef = React.useRef<AbortController | null>(null);
  const executionStepsRef = React.useRef<ExecutionStep[]>([]);
  const currentMessageRef = React.useRef<StreamingMessage | null>(null);
  const startedAtRef = React.useRef<number | null>(null);
  const contextRef = React.useRef<StartStreamParams | null>(null);
  const finalResolveRef = React.useRef<((value: StreamFinalize) => void) | null>(null);
  const finalRejectRef = React.useRef<((reason?: unknown) => void) | null>(null);

  const updateExecutionSteps = React.useCallback(
    (updater: (prev: ExecutionStep[]) => ExecutionStep[]) => {
      setExecutionSteps((prev) => {
        const next = updater(prev);
        executionStepsRef.current = next;
        return next;
      });
    },
    []
  );

  const updateCurrentMessage = React.useCallback(
    (updater: (prev: StreamingMessage | null) => StreamingMessage | null) => {
      setCurrentMessage((prev) => {
        const next = updater(prev);
        currentMessageRef.current = next;
        return next;
      });
    },
    []
  );

  const parseSSEEvent = React.useCallback((chunk: string) => {
    let eventType = 'message';
    const dataLines: string[] = [];

    chunk.split('\n').forEach((line) => {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5));
      } else if (line.startsWith(':')) {
        // comment/heartbeat line, ignore
      }
    });

    const payload = dataLines.join('\n').trim();
    let parsed: any = null;
    if (payload.length > 0) {
      try {
        parsed = JSON.parse(payload);
      } catch {
        parsed = payload;
      }
    }

    return { eventType, data: parsed };
  }, []);

  const resolveFinal = React.useCallback(
    (finalPayload: any, finalMessage: StreamingMessage | null) => {
      if (finalResolveRef.current) {
        finalResolveRef.current({
          finalMessage,
          payload: finalPayload,
          executionSteps: executionStepsRef.current,
        });
      }
      finalResolveRef.current = null;
      finalRejectRef.current = null;
    },
    []
  );

  const rejectFinal = React.useCallback((reason?: unknown) => {
    if (finalRejectRef.current) {
      finalRejectRef.current(reason);
    }
    finalResolveRef.current = null;
    finalRejectRef.current = null;
  }, []);

  const handleStreamEvent = React.useCallback(
    (eventType: string, data: any) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[useAgentStreaming] event', eventType, data);
      }
      switch (eventType) {
        case 'planner_decision': {
          if (!data) break;
          const iteration = Number(
            data.iteration ?? data.decision?.iteration ?? executionStepsRef.current.length + 1
          );
          updateExecutionSteps((prev) => [
            ...prev,
            {
              id: `decision-${iteration}-${Date.now()}`,
              type: 'planner_decision',
              status: 'complete',
              timestamp: Date.now(),
              iteration,
              data,
            },
          ]);
          updateCurrentMessage((prev) =>
            prev
              ? {
                  ...prev,
                  plannerIteration: iteration,
                  plannerDecision: data.decision ?? data,
                }
              : prev
          );
          break;
        }
        case 'planning.start': {
          if (!data) break;
          updateExecutionSteps((prev) => [
            ...prev,
            {
              id: `planning-start-${Date.now()}`,
              type: 'planner_decision',
              status: 'complete',
              timestamp: Date.now(),
              iteration: 0,
              data: { decision: 'start', ...data },
            },
          ]);
          break;
        }
        case 'planning.iteration': {
          if (!data) break;
          const iteration = Number(data.iteration ?? executionStepsRef.current.length + 1);
          updateExecutionSteps((prev) => [
            ...prev,
            {
              id: `planning-iter-${iteration}-${Date.now()}`,
              type: 'planner_decision',
              status: 'complete',
              timestamp: Date.now(),
              iteration,
              data, // Preserves reasoning, reflection_text, and all other fields
            },
          ]);
          updateCurrentMessage((prev) =>
            prev
              ? {
                  ...prev,
                  plannerIteration: iteration,
                  plannerDecision: data.decision ?? 'iteration',
                }
              : prev
          );
          break;
        }
        case 'planning.reflection': {
          if (!data) break;
          const iteration = Number(data.iteration ?? executionStepsRef.current.length);
          updateExecutionSteps((prev) => [
            ...prev,
            {
              id: `planning-reflection-${iteration}-${Date.now()}`,
              type: 'planner_decision',
              status: 'complete',
              timestamp: Date.now(),
              iteration,
              data: { decision: 'reflect', ...data },
            },
          ]);
          break;
        }
        case 'planning.complete': {
          if (!data) break;
          updateExecutionSteps((prev) => [
            ...prev,
            {
              id: `planning-complete-${Date.now()}`,
              type: 'planner_decision',
              status: 'complete',
              timestamp: Date.now(),
              data: { decision: 'complete', ...data },
            },
          ]);
          break;
        }
        case 'execution.start': {
          if (!data) break;
          updateExecutionSteps((prev) => [
            ...prev,
            {
              id: `execution-start-${Date.now()}`,
              type: 'executor_content',
              status: 'in_progress',
              timestamp: Date.now(),
              data,
            },
          ]);
          break;
        }
        case 'execution.complete': {
          if (!data) break;
          updateExecutionSteps((prev) => [
            ...prev,
            {
              id: `execution-complete-${Date.now()}`,
              type: 'executor_content',
              status: 'complete',
              timestamp: Date.now(),
              data,
            },
          ]);
          break;
        }
        case 'tool_call': {
          if (!data?.tool) break;
          const callName = data.tool.name ?? 'unknown';
          updateExecutionSteps((prev) => [
            ...prev,
            {
              id: `tool-${Date.now()}`,
              type: 'tool_call',
              status: 'in_progress',
              timestamp: Date.now(),
              iteration: data.iteration,
              data: data.tool,
            },
          ]);
          updateCurrentMessage((prev) => {
            if (!prev) return prev;
            const toolCalls = Array.isArray(prev.toolCalls) ? [...prev.toolCalls] : [];
            toolCalls.push({
              name: callName,
              args: data.tool.arguments ?? data.tool.args ?? {},
              status: 'executing',
            });
            const inProgress = Array.isArray(prev.toolCallsInProgress)
              ? [...prev.toolCallsInProgress, callName]
              : [callName];
            return {
              ...prev,
              toolCalls,
              toolCallsInProgress: inProgress,
            };
          });
          break;
        }
        case 'tool_result': {
          if (!data) break;
          const toolName = data.tool ?? data.name ?? 'unknown';
          const status: ExecutionStepStatus = data.error ? 'error' : 'complete';
          updateExecutionSteps((prev) => {
            const next = prev.map((step, _idx) => {
              if (
                step.type === 'tool_call' &&
                step.status === 'in_progress' &&
                step.data?.name === toolName
              ) {
                return {
                  ...step,
                  status,
                  duration: data.duration ?? step.duration,
                  data: { ...(step.data ?? {}), result: data.result, error: data.error },
                };
              }
              return step;
            });
            next.push({
              id: `tool-result-${Date.now()}`,
              type: 'tool_result',
              status,
              timestamp: Date.now(),
              iteration: data.iteration,
              duration: data.duration,
              data,
            });
            return next;
          });
          updateCurrentMessage((prev) => {
            if (!prev) return prev;
            const toolCalls = Array.isArray(prev.toolCalls) ? [...prev.toolCalls] : [];
            for (let i = toolCalls.length - 1; i >= 0; i -= 1) {
              if (toolCalls[i]?.name === toolName && toolCalls[i]?.status === 'executing') {
                toolCalls[i] = {
                  ...toolCalls[i],
                  status: data.error ? 'error' : 'complete',
                  output: data.result,
                  error: data.error,
                  duration: data.duration,
                };
                break;
              }
            }
            const inProgress = (prev.toolCallsInProgress ?? []).filter((name) => name !== toolName);
            return {
              ...prev,
              toolCalls,
              toolCallsInProgress: inProgress,
            };
          });
          break;
        }
        case 'executor_content': {
          if (!data?.text) break;
          updateExecutionSteps((prev) => {
            const lastStep = prev[prev.length - 1];
            if (
              lastStep &&
              lastStep.type === 'executor_content' &&
              lastStep.status === 'in_progress'
            ) {
              const next = [...prev];
              next[next.length - 1] = {
                ...lastStep,
                timestamp: Date.now(),
                data: {
                  ...lastStep.data,
                  text: (lastStep.data?.text ?? '') + data.text,
                },
              };
              return next;
            }
            return [
              ...prev,
              {
                id: `executor-${Date.now()}`,
                type: 'executor_content',
                status: 'in_progress',
                timestamp: Date.now(),
                data,
              },
            ];
          });
          updateCurrentMessage((prev) => {
            const base = prev ?? {
              role: 'assistant',
              content: '',
              timestamp: Date.now(),
              isStreaming: true,
              streamingContent: '',
            };
            return {
              ...base,
              isStreaming: true,
              streamingContent: (base.streamingContent ?? base.content ?? '') + data.text,
              content: (base.content ?? '') + data.text,
            };
          });
          break;
        }
        case 'final': {
          const finishedAt = Date.now();
          const elapsed = startedAtRef.current ? finishedAt - startedAtRef.current : undefined;
          let computedMessage: StreamingMessage | null = null;
          updateCurrentMessage((prev) => {
            const base = prev ?? {
              role: 'assistant',
              content: '',
              timestamp: finishedAt,
            };
            const contentFromPayload =
              data?.messages?.[0]?.content ??
              (Array.isArray(data?.messages) ? data.messages[0]?.content : null);
            computedMessage = {
              ...base,
              content: typeof contentFromPayload === 'string' ? contentFromPayload : base.content,
              provider: base.provider ?? contextRef.current?.provider,
              model: base.model ?? contextRef.current?.model,
              documents: Array.isArray(data?.results?.documents)
                ? data.results.documents
                : base.documents,
              toolCalls: Array.isArray(data?.toolCalls) ? data.toolCalls : base.toolCalls,
              latency: elapsed ?? base.latency,
              error: data?.error ?? base.error,
              isStreaming: false,
              streamingContent: undefined,
              toolCallsInProgress: [],
            };
            return computedMessage;
          });

          let computedSteps: ExecutionStep[] = [];
          updateExecutionSteps((prev) => {
            const normalized: ExecutionStep[] = prev.map((step): ExecutionStep => {
              if (step.status === 'in_progress') {
                const updated: ExecutionStep = {
                  ...step,
                  status: 'complete' as ExecutionStepStatus,
                };
                return updated;
              }
              return step;
            });
            const finalStep: ExecutionStep = {
              id: `final-${finishedAt}`,
              type: 'executor_content',
              status: 'complete' as ExecutionStepStatus,
              timestamp: finishedAt,
              duration: elapsed,
              data,
            };
            computedSteps = [...normalized, finalStep];
            return computedSteps;
          });

          resolveFinal(data, computedMessage);
          break;
        }
        case 'error': {
          const message = typeof data?.error === 'string' ? data.error : 'Streaming error';
          setError(message);
          updateExecutionSteps((prev) => [
            ...prev,
            {
              id: `error-${Date.now()}`,
              type: 'executor_content',
              status: 'error',
              timestamp: Date.now(),
              data,
            },
          ]);
          updateCurrentMessage((prev) =>
            prev
              ? {
                  ...prev,
                  isStreaming: false,
                  error: message,
                }
              : prev
          );
          rejectFinal(new Error(message));
          break;
        }
        case 'end':
        default:
          break;
      }
    },
    [rejectFinal, resolveFinal, updateCurrentMessage, updateExecutionSteps]
  );

  const startStream = React.useCallback(
    async (params: StartStreamParams): Promise<StreamFinalize> => {
      if (isStreaming) {
        throw new Error('Streaming already in progress');
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      contextRef.current = params;
      setIsStreaming(true);
      setError(null);
      setExecutionSteps([]);
      setCurrentMessage(null);
      setStartedAt(Date.now());
      setLastEventAt(Date.now());
      startedAtRef.current = Date.now();
      executionStepsRef.current = [];
      currentMessageRef.current = null;

      const initialAssistant: StreamingMessage = {
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        provider: params.provider,
        model: params.model,
        isStreaming: true,
        streamingContent: '',
        toolCallsInProgress: [],
      };
      updateCurrentMessage(() => initialAssistant);

      try {
        const headers = await tokenService.buildAuthHeaders();
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json', Accept: 'text/event-stream' },
          body: JSON.stringify({
            provider: params.provider,
            model: params.model,
            message: params.message,
            personId: params.personId,
            tools: params.tools,
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`Stream failed: ${res.status}`);
        }

        if (!res.body) {
          throw new Error('Stream response missing body');
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        return await new Promise<StreamFinalize>((resolve, reject) => {
          finalResolveRef.current = resolve;
          finalRejectRef.current = reject;

          const pump = async (): Promise<void> => {
            try {
              const { done, value } = await reader.read();
              if (done) {
                resolveFinal(null, currentMessageRef.current);
                return;
              }

              buffer += decoder.decode(value, { stream: true });
              let boundary = buffer.indexOf('\n\n');
              while (boundary !== -1) {
                const rawEvent = buffer.slice(0, boundary);
                buffer = buffer.slice(boundary + 2);
                if (rawEvent.trim().length > 0) {
                  const { eventType, data } = parseSSEEvent(rawEvent);
                  handleStreamEvent(eventType, data);
                  setLastEventAt(Date.now());
                }
                boundary = buffer.indexOf('\n\n');
              }

              await pump();
            } catch (err) {
              if ((err as DOMException)?.name === 'AbortError') {
                rejectFinal(new Error('Stream aborted'));
              } else {
                rejectFinal(err);
              }
            }
          };

          pump();
        });
      } catch (err) {
        rejectFinal(err);
        throw err;
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [
      endpoint,
      handleStreamEvent,
      isStreaming,
      parseSSEEvent,
      rejectFinal,
      resolveFinal,
      updateCurrentMessage,
    ]
  );

  const stopStream = React.useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const resetStream = React.useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setExecutionSteps([]);
    setCurrentMessage(null);
    setError(null);
    setStartedAt(null);
    setLastEventAt(null);
    startedAtRef.current = null;
    executionStepsRef.current = [];
    currentMessageRef.current = null;
    contextRef.current = null;
    finalResolveRef.current = null;
    finalRejectRef.current = null;
  }, []);

  React.useEffect(() => {
    if (!isStreaming) {
      return undefined;
    }

    const watchdog = window.setInterval(() => {
      if (!lastEventAt) return;
      if (Date.now() - lastEventAt > stallTimeoutMs) {
        setError('Stream stalled (no events received)');
        stopStream();
        rejectFinal(new Error('Stream stalled'));
      }
    }, watchdogIntervalMs);

    return () => window.clearInterval(watchdog);
  }, [isStreaming, lastEventAt, rejectFinal, stallTimeoutMs, stopStream, watchdogIntervalMs]);

  return {
    isStreaming,
    executionSteps,
    currentMessage,
    error,
    startStream,
    stopStream,
    resetStream,
  };
}

export default useAgentStreaming;
